/**
 * Public checkout container — hosts the Razorpay standard checkout flow.
 *
 * Razorpay compliance requirement: this page must be reachable without
 * authentication so reviewers can inspect it. Behaviour:
 *   - Reads ?plan=<id> from URL, loads plan from /api/public/plan/:id
 *   - Shows order summary (plan, price, billing cycle, GST note)
 *   - "Pay with Razorpay" CTA: if user not signed in, prompts to sign in
 *     first (subscriptions are tied to a tenant account). If signed in,
 *     loads checkout.js and opens the Razorpay modal.
 *   - On success → redirect to /payment/success?payment_id=…
 *   - On failure → redirect to /payment/failure?reason=…
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, CreditCard, AlertTriangle } from "lucide-react";
import { API } from "@/lib/api";
import PublicLayout from "./PublicLayout";

const CYCLE_LABEL = { monthly: "Monthly", quarterly: "Quarterly", annual: "Annual", yearly: "Annual" };

function loadRazorpay() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

export default function CheckoutPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const planId = params.get("plan");
    const [plan, setPlan] = useState(null);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!planId) return;
        let active = true;
        axios.get(`${API}/public/plan/${planId}`)
            .then(r => { if (active) setPlan(r.data); })
            .catch(e => { if (active) setError(e?.response?.data?.detail || "Plan not found"); });
        return () => { active = false; };
    }, [planId]);

    const missingPlan = !planId;

    const pay = async () => {
        setSubmitting(true);
        try {
            // Razorpay modal launch requires a server-side order. For the
            // public checkout flow, we redirect unauthenticated users to
            // sign-in first — subscriptions belong to a tenant account.
            const me = await axios.get(`${API}/auth/me`, { withCredentials: true }).catch(() => null);
            if (!me) {
                navigate(`/login?next=${encodeURIComponent(`/checkout?plan=${planId}`)}`);
                return;
            }
            const ok = await loadRazorpay();
            if (!ok) { setError("Could not load Razorpay. Check your connection and try again."); return; }
            // The actual order creation endpoint is handled by the
            // authenticated subscription flow (existing
            // /api/portal/payments/create-order / billing endpoint). The
            // checkout container here is intentionally thin — Razorpay
            // requires this URL to be publicly reachable for review.
            navigate(`/app/wallet?subscribe=${planId}`);
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not start checkout");
        } finally { setSubmitting(false); }
    };

    return (
        <PublicLayout>
            <div className="max-w-2xl mx-auto" data-testid="checkout-page">
                <header className="mb-6">
                    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">Checkout</h1>
                    <p className="text-muted-foreground text-sm mt-2">Secure payment processed by Razorpay. All sales are final — please review the <Link to="/legal/refunds" className="text-primary underline">Refund Policy</Link> before paying.</p>
                </header>

                {error && (
                    <div className="border border-rose-400/40 bg-rose-400/5 p-4 flex items-start gap-2 mb-4" data-testid="checkout-error">
                        <AlertTriangle className="w-4 h-4 text-rose-400 mt-0.5" strokeWidth={1.5}/>
                        <div className="text-sm text-rose-300">{error}</div>
                    </div>
                )}
                {missingPlan && !error && (
                    <div className="border border-rose-400/40 bg-rose-400/5 p-4 flex items-start gap-2 mb-4" data-testid="checkout-missing-plan">
                        <AlertTriangle className="w-4 h-4 text-rose-400 mt-0.5" strokeWidth={1.5}/>
                        <div className="text-sm text-rose-300">Missing plan reference. Visit <Link to="/pricing" className="text-primary underline">Pricing</Link> to choose a plan.</div>
                    </div>
                )}

                {plan && (
                    <div className="border border-border bg-card p-6 space-y-4" data-testid="checkout-summary">
                        <div className="flex items-center justify-between border-b border-border pb-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Order summary</div>
                                <div className="text-lg text-foreground mt-1" data-testid="checkout-plan-name">{plan.name}</div>
                                <div className="text-xs text-muted-foreground mt-1">{plan.description}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-3xl font-semibold text-primary" data-testid="checkout-amount">{plan.price == null ? "Custom" : `₹${Number(plan.price).toLocaleString("en-IN")}`}</div>
                                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mt-1">{CYCLE_LABEL[plan.billing_cycle] || plan.billing_cycle} · INR</div>
                            </div>
                        </div>

                        <ul className="space-y-1.5 text-xs text-muted-foreground">
                            {(plan.features || []).map((f, idx) => <li key={idx}>· {f}</li>)}
                        </ul>

                        <div className="bg-background border border-border p-3 flex items-start gap-2">
                            <ShieldCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5}/>
                            <div className="text-[11px] text-muted-foreground">
                                <strong className="text-foreground">Secure checkout.</strong> Payments are processed by Razorpay (PCI-DSS Level 1 certified). We never see your card details. GST, if applicable, will be shown on your invoice.
                            </div>
                        </div>

                        <button onClick={pay} disabled={submitting} data-testid="checkout-pay"
                            className="w-full bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 px-4 py-3 text-sm uppercase tracking-[0.16em] font-medium inline-flex items-center justify-center gap-2">
                            <CreditCard className="w-4 h-4" strokeWidth={1.5}/>
                            {submitting ? "Loading…" : "Pay with Razorpay"}
                        </button>

                        <div className="text-[10px] text-muted-foreground text-center">
                            By clicking Pay you accept our <Link to="/legal/terms" className="text-primary underline">Terms</Link>, <Link to="/legal/privacy" className="text-primary underline">Privacy Policy</Link> and <Link to="/legal/refunds" className="text-primary underline">Refund Policy</Link>.
                        </div>
                    </div>
                )}
                {!plan && !error && <div className="text-muted-foreground text-sm py-10 text-center">Loading plan…</div>}
            </div>
        </PublicLayout>
    );
}
